<template>
  <q-card
    flat
    bordered
    class="bg-grey-1 q-py-md"
    :class="{ 'cursor-pointer': !editing }"
    style="width: 100%"
    @click="requestEdit"
  >
    <q-form
      ref="formRef"
    >
      <q-list>
        <div class="row">
          <q-item class="col-6">
            <q-item-section>
              <q-item-label>Seat Type</q-item-label>
              <q-item-label
                v-if="!editing"
                caption
              >
                {{ formatName(override.seatType) }}
              </q-item-label>
              <q-item-label v-else>
                <q-select
                  v-model="override.seatType"
                  class="col-6"
                  :options="$mh.asOptions(SEAT_TYPES.OVERRIDES_OPTIONS)"
                  map-options
                  emit-value
                  clearable
                >
                  <template #option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      v-on="scope.itemEvents"
                    >
                      <q-item-section>
                        <div class="row items-center justify-between q-gutter-x-sm">
                          <span>{{ scope.opt.label }}</span>
                          <div
                            style="max-width: 35px;"
                            class="seat col-auto"
                          >
                            <div
                              class="seat-content"
                              :class="[override.class, override.seatType || 'regular-seat']"
                              :style="override.customSeatTypeColor ? `color: ${override.customSeatTypeColor} !important;` : ''"
                            >
                              <svg viewBox="0 0 220 220">
                                <use :xlink:href="'/seats.svg#' + scope.opt.value" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item class="col-6">
            <q-item-section>
              <q-item-label>Available</q-item-label>
              <q-item-label caption>
                <q-item-label
                  v-if="!editing"
                  caption
                >
                  {{ formatName(override.available) }}
                </q-item-label>
                <q-checkbox
                  v-else
                  v-model="override.available"
                  class="col-6"
                  :disable="!editing"
                  toggle-indeterminate
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="override.available === false"
          class="row"
        >
          <q-item
            class="col-12"
          >
            <q-item-section>
              <q-item-label>Availability Type</q-item-label>
              <q-item-label
                v-if="!editing"
                caption
              >
                {{ formatName(override.availabilityType) }}
              </q-item-label>
              <q-item-label v-else>
                <q-select
                  v-model="override.availabilityType"
                  class="col-6"
                  :options="$mh.asOptions(AVAILABILITY_TYPES.OPTIONS)"
                  map-options
                  emit-value
                  clearable
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="override.available === false && !!override.availabilityType"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>{{ formatName(override.availabilityType) }} At</q-item-label>
              <q-item-label caption>
                <q-item-label
                  v-if="!editing"
                  caption
                >
                  {{ formatDateTime(override.availableStartTime) }}
                </q-item-label>
                <q-input
                  v-else
                  class="col-6"
                  :model-value="formatDateTime(override.availableStartTime)"
                  clearable
                  @clear="override.availableStartTime = null"
                >
                  <template #prepend>
                    <q-icon
                      name="event"
                      class="cursor-pointer"
                    >
                      <q-popup-proxy
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          :model-value="date.formatDate(override.availableStartTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                          flat
                          mask="YYYY-MM-DDTHH:mm:ssZ"
                          @update:model-value="value => override.availableStartTime = date.extractDate(value, 'YYYY-MM-DDTHH:mm:ssZ')"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                  <template #append>
                    <q-icon
                      name="access_time"
                      class="cursor-pointer"
                    >
                      <q-popup-proxy
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-time
                          :model-value="date.formatDate(override.availableStartTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                          flat
                          mask="YYYY-MM-DDTHH:mm:ssZ"
                          @update:model-value="value => override.availableStartTime = date.extractDate(value, 'YYYY-MM-DDTHH:mm:ssZ')"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>{{ formatName(override.availabilityType) }} Until</q-item-label>
              <q-item-label caption>
                <q-item-label
                  v-if="!editing"
                  caption
                >
                  {{ formatDateTime(override.availableEndTime) }}
                </q-item-label>
                <q-input
                  v-else
                  class="col-6"
                  :model-value="formatDateTime(override.availableEndTime)"
                  clearable
                  @clear="override.availableEndTime = null"
                >
                  <template #prepend>
                    <q-icon
                      name="event"
                      class="cursor-pointer"
                    >
                      <q-popup-proxy
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          :model-value="date.formatDate(override.availableEndTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                          flat
                          mask="YYYY-MM-DDTHH:mm:ssZ"
                          @update:model-value="value => override.availableEndTime = date.extractDate(value, 'YYYY-MM-DDTHH:mm:ssZ')"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                  <template #append>
                    <q-icon
                      name="access_time"
                      class="cursor-pointer"
                    >
                      <q-popup-proxy
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-time
                          :model-value="date.formatDate(override.availableEndTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                          flat
                          mask="YYYY-MM-DDTHH:mm:ssZ"
                          @update:model-value="value => override.availableEndTime = date.extractDate(value, 'YYYY-MM-DDTHH:mm:ssZ')"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div class="row">
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Custom Seat Name</q-item-label>
              <q-item-label
                v-if="!editing"
                caption
              >
                {{ formatName(override.seatName) }}
              </q-item-label>
              <q-item-label v-else>
                <q-input
                  v-model="override.seatName"
                  :disable="!editing"
                  mask="X"
                  reverse-fill-mask
                  clearable
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="editing || override.reserveDuringMarathon !== null"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Reserve During Marathons</q-item-label>
              <q-item-label caption>
                <q-item-label
                  v-if="!editing"
                  caption
                >
                  {{ formatName(override.reserveDuringMarathons) }}
                </q-item-label>
                <q-checkbox
                  v-else
                  v-model="override.reserveDuringMarathons"
                  class="col-6"
                  :disable="!editing"
                  toggle-indeterminate
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="editing || override.limitTicketTypes !== null"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Limit Tickets</q-item-label>
              <q-item-label caption>
                <q-item-label
                  v-if="!editing"
                  caption
                >
                  {{ formatName(override.limitTicketTypes) }}
                </q-item-label>
                <q-checkbox
                  v-else
                  v-model="override.limitTicketTypes"
                  class="col-6"
                  :disable="!editing"
                  toggle-indeterminate
                />
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-if="override.limitTicketTypes"
            class="col-12"
          >
            <q-item-section>
              <q-item-label>Ticket Types</q-item-label>
              <q-item-label
                v-if="!editing"
                caption
              >
                {{ ticketTypeNames(override.ticketTypeIds) }}
              </q-item-label>
              <q-item-label
                v-else
              >
                <q-select
                  v-model="override.ticketTypeIds"
                  class="col-6"
                  label="Ticket Types"
                  :options="limitTicketTypeOptions"
                  map-options
                  emit-value
                  clearable
                  multiple
                  use-chips
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="editing || override.customSeatType"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Custom Seat Type</q-item-label>
              <q-item-label>
                <q-checkbox
                  v-model="override.customSeatType"
                  class="col-6"
                  :disable="!editing"
                  toggle-indeterminate
                  clearable
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="editing || override.heldSeatSectionName"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Held Seats Section Name</q-item-label>
              <q-item-label>
                <q-input
                  v-model="override.heldSeatSectionName"
                  class="col-12"
                  :disable="!editing"
                  clearable
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="override.customSeatType"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Custom Seat Type Name</q-item-label>
              <q-item-label>
                <q-input
                  v-model="override.customSeatTypeName"
                  class="col-12"
                  :disable="!editing"
                  :rules="[val => !!val]"
                  clearable
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          v-if="override.customSeatType"
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Custom Seat Color</q-item-label>
              <q-item-label>
                <div class="row">
                  <div
                    style="max-width: 35px;"
                    class="seat col-2"
                  >
                    <div
                      class="seat-content q-mt-sm"
                      :class="[override.class, override.seatType || 'regular-seat']"
                      :style="override.customSeatTypeColor ? `color: ${override.customSeatTypeColor} !important;` : ''"
                    >
                      <svg viewBox="0 0 220 220">
                        <use :xlink:href="'/seats.svg#' + (override.seatType || 'regular-seat')" />
                      </svg>
                    </div>
                  </div>
                  <q-input
                    v-model="override.customSeatTypeColor"
                    filled
                    class="col-6 q-ml-sm"
                    :rules="['anyColor']"
                  >
                    <template #append>
                      <q-icon
                        name="colorize"
                        class="cursor-pointer"
                      >
                        <q-popup-proxy
                          cover
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-color
                            v-model="override.customSeatTypeColor"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                  </q-input>
                </div>
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div
          class="row"
        >
          <q-item class="col-12">
            <q-item-section>
              <q-item-label>Require Confirmation</q-item-label>
              <q-item-label>
                <q-item-label
                  v-if="!editing"
                  caption
                >
                  {{ formatName(override.confirmationRequired) }}
                </q-item-label>
                <q-checkbox
                  v-else
                  v-model="override.confirmationRequired"
                  class="col-6"
                  :disable="!editing"
                  toggle-indeterminate
                />
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-if="override.confirmationRequired"
            class="col-12"
          >
            <q-item-section>
              <q-item-label>Confirmation Message</q-item-label>
              <q-item-label>
                <q-input
                  v-model="override.confirmationMessage"
                  type="textarea"
                  class="col-12"
                  :disable="!editing"
                  :rules="[val => !!val]"
                />
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div class="row">
          <q-item :class="[editing ? 'col-12' : 'col-6']">
            <q-item-section>
              <q-item-label>Row</q-item-label>
              <q-item-label
                v-if="!editing"
                caption
              >
                {{ formatRange(override.rowStart, override.rowEnd) }}
              </q-item-label>
              <div
                v-else
                class="row no-wrap q-gutter-xs"
              >
                <q-select
                  v-model="override.rowStart"
                  class="col-6"
                  label="From"
                  dense
                  clearable
                  :options="rowFromOptions"
                />
                <q-select
                  v-model="override.rowEnd"
                  class="col-6"
                  label="To"
                  dense
                  :options="rowToOptions"
                />
              </div>
            </q-item-section>
          </q-item>
          <q-item :class="[editing ? 'col-12' : 'col-6']">
            <q-item-section>
              <q-item-label>Column</q-item-label>
              <q-item-label
                v-if="!editing"
                caption
              >
                {{ formatRange(override.columnStart, override.columnEnd) }}
              </q-item-label>
              <div
                v-else
                class="row no-wrap q-gutter-xs"
              >
                <q-input
                  v-model.number="override.columnStart"
                  class="col-6"
                  label="From"
                  dense
                  type="number"
                  :min="1"
                  clearable
                />
                <q-input
                  v-model.number="override.columnEnd"
                  class="col-6"
                  label="To"
                  dense
                  type="number"
                  :min="+override.columnStart + 1"
                />
              </div>
            </q-item-section>
          </q-item>
        </div>
      </q-list>
      <q-card-actions
        v-if="editing"
        class="row justify-evenly"
      >
        <q-btn
          label="Done"
          class="self-center"
          color="primary"
          @click.stop="doneEditing"
        />
        <q-btn
          label="Delete"
          class="self-center"
          color="secondary"
          @click.stop="$emit('delete-override', override.id)"
        />
      </q-card-actions>
    </q-form>
  </q-card>
</template>

<script>
import { date } from 'quasar'
import { IsoDateTime } from 'src/models/Model'
import { AVAILABILITY_TYPES, SEAT_TYPES } from 'src/models/screen/SeatChart'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'

const charMap = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

export default {
  name: 'Override',
  mixins: [FormValidationMixin],
  props: {
    rowCount: {
      type: Number,
      required: true
    },
    columnCount: {
      type: Number,
      required: true
    },
    override: {
      type: Object,
      required: true
    },
    editingId: {
      type: Number,
      required: false,
      default: null
    }
  },
  emits: ['delete-override', 'edit'],
  data () {
    return {
      menu: false
    }
  },
  configuration: ['ticketTypes'],
  computed: {
    rowFromOptions () {
      return _.map(_.range(0, this.rowCount), (rowIndex) => {
        return charMap[rowIndex]
      })
    },
    rowToOptions () {
      return _.map(_.range(0, this.rowCount), (rowIndex) => {
        return charMap[rowIndex]
      })
    },
    editing () {
      return this.override.id === this.editingId
    },
    singleSelection () {
      return !(this.override.columnEnd && this.override.columnEnd !== this.override.columnStart) &&
      !(this.override.rowEnd && this.override.rowEnd !== this.override.rowStart)
    },
    limitTicketTypeOptions () {
      return _(this.ticketTypes.data)
        .filter('appliesToSpecificSeats')
        .filter(ticketType => !ticketType.isArchived)
        .map(ticketType => ({ label: ticketType.displayName, value: ticketType.id }))
        .value()
    }
  },
  watch: {
    'override.columnStart' () {
      if (this.override.columnStart === null) {
        this.override.columnEnd = null
      }
    },
    'override.rowStart' () {
      if (this.override.rowStart === null) {
        this.override.rowEnd = null
      }
    }
  },
  created () {
    _.extend(this, { AVAILABILITY_TYPES, date, SEAT_TYPES })
  },
  methods: {
    formatRange (start, end) {
      if (start === null && end === null) return 'All'

      if (start === end || end === null) return start

      return `${start} to ${end}`
    },
    formatName (name) {
      if (_.isNil(name)) return '<no change>'

      return _.startCase(name)
    },
    formatDateTime (dateTime) {
      if (!dateTime) return '<no change>'

      return IsoDateTime.parse(dateTime).i18n('dateTime')
    },
    ticketTypeNames (ids) {
      return _(this.ticketTypes.data).filter(ticketType => _.includes(ids, ticketType.id)).map('name')
    },
    requestEdit () {
      this.$emit('edit', this.override.id)
    },
    doneEditing () {
      this.validateForm(() => {
        this.$emit('edit', null)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .seat {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
    fill-opacity: 0;
  }
  .seat-content {
    width: 100%;
    position: relative;
  }
  svg {
    width: 100%;
    height: auto;
  }
  .seat-content {
    padding: 20% 5%;
  }
  .seat-content.regular-seat {
    padding: 20% 10%;
  }

  .seat-content.couch-left, .seat-content.couch-corner-left {
    padding: 20% 0% 20% 10%;
  }

  .seat-content.couch-vertical-east-left {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-right, .seat-content.couch-corner-right {
    padding: 20% 10% 20% 0%;
  }

  .seat-content.couch-vertical-west-right {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-center {
    padding: 20% 0%;
  }

  .seat-content.couch-vertical-east-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.couch-vertical-west-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.table, .seat-content.table-left, .seat-content.table-right, .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .seat-content.table-left {
    padding: 30% 0 0 0;
  }

  .seat-content.table-right {
    padding: 30% 0 0 0;
  }

  .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .show-scrollbars {
    overflow: auto;
  }

  .social-distancing {
    opacity: 0.4
  }

</style>
